import React, { useEffect, useState } from "react";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import { Header, MetricEdgeProto, MetricProto } from "build-ts/metadata";
import { MetricEdgeType } from "build-ts/enums";
import { getAllBenchmarks, listMetrics, updateMetric } from "services/MetricService";
import showToast from "hooks/useCustomToast";
import { getAllKGColumns } from "services/DataModelService";
import EditMKGComponent from "components/configuration/mkg/EditMKGComponent";
import { useConfigurationContext } from "context/ConfigurationContext";

interface EditMKGContainerProps {
  metricInfo: CreateOrUpdateMetrics_Request | null;

  // For saving the MKG
  triggerSubmit?: boolean;
  setTriggerSubmit?: (value: boolean) => void;
  onReloadKnowledgeGraph?: () => void;
}

const EditMKGContainer: React.FC<EditMKGContainerProps> = ({ metricInfo, triggerSubmit, setTriggerSubmit, onReloadKnowledgeGraph }) => {
  const [allMetrics, setAllMetrics] = useState<Header[] | null>(null);
  const [allBenchmarks, setAllBenchmarks] = useState<Header[] | null>(null);
  const [allKGColumns, setAllKGColumns] = useState<Header[] | null>(null);

  const [selectedMetricHeader, setSelectedMetricHeader] = useState<Header[]>([]);
  const [selectedBenchmarkHeader, setSelectedBenchmarkHeader] = useState<Header[]>([]);
  const [selectedAttributeHeader, setSelectedAttributeHeader] = useState<Header[]>([]);
  const [selectedMeasureHeader, setSelectedMeasureHeader] = useState<Header[]>([]);
  const [selectedTemporalHeader, setSelectedTemporalHeader] = useState<Header[]>([]);

  const [benchmarkEdge, setBenchmarkEdge] = useState<MetricEdgeProto[]>([]);
  const [kgColumnEdge, setKgColumnEdge] = useState<MetricEdgeProto[]>([]);
  const [metricEdge, setMetricEdge] = useState<MetricEdgeProto[]>([]);

  const {onClose} = useConfigurationContext();

  // Fetching the edges from the metricInfo
  useEffect(() => {
    if(metricInfo && metricInfo.metrics[0] && metricInfo.metrics[0].edges) {
      const metricEdges : MetricEdgeProto[] = metricInfo.metrics[0].edges
        .filter((edge) => edge.edge_type === MetricEdgeType.METRIC_METRIC_EDGE)
        .map((edge) => ({
            edge_type: edge.edge_type,
            header: edge.header,
            source_metric_id: edge.source_metric_id
        }));
        setMetricEdge(metricEdges);
    }
    if(metricInfo && metricInfo.metrics[0] && metricInfo.metrics[0].edges) {
      const benchmarkEdges: MetricEdgeProto[] = metricInfo.metrics[0].edges
        .filter((edge) => edge.edge_type === MetricEdgeType.BENCHMARK_EDGE || edge.edge_type === MetricEdgeType.METRIC_BENCHMARK_EDGE)
        .map((edge) => ({
          edge_type: edge.edge_type,
          benchmark: edge?.benchmark,
          source_metric_id: edge?.source_metric_id,
          header: edge?.header
        }));
        setBenchmarkEdge(benchmarkEdges);
    }
    if(metricInfo && metricInfo.metrics[0] && metricInfo.metrics[0].edges) {
      const kgColumnEdges: MetricEdgeProto[] = metricInfo.metrics[0].edges
        .filter((edge) => edge.edge_type === MetricEdgeType.METRIC_ATTRIBUTE_EDGE || edge.edge_type === MetricEdgeType.METRIC_MEASURE_EDGE || edge.edge_type === MetricEdgeType.METRIC_TIMEFRAME_EDGE)
        .map((edge) => ({
            edge_type: edge.edge_type,
            header: edge.header,
            source_metric_id: edge.source_metric_id
        }));
        setKgColumnEdge(kgColumnEdges);
    }
  }, [metricInfo]);

  useEffect(()=> {
    const fetchMetrics = async() => {
      try{
        const data = await listMetrics(""/* modelId */);
        setAllMetrics(data);
      } catch (error) {
        showToast("Error fetching data", "", "error");
      }
    };

    const fetchBenchmarks = async() => {
      try {
        const benchmarks = await getAllBenchmarks();
        setAllBenchmarks(benchmarks);
      } catch (error) {
        showToast("Error fetching benchmarks", "", "error");
      }
    };

    const fetchAllKGColumns = async () => {
        const kgColumns = await getAllKGColumns();
        setAllKGColumns(kgColumns);
    };

    fetchMetrics();
    fetchBenchmarks();
    fetchAllKGColumns();
  },[]);

  const handleMetricChange = (metric: Header, checked: boolean) => {
    setSelectedMetricHeader(prev => 
      checked 
        ? [...prev, metric]
        : prev.filter(h => h.id !== metric.id)
    );
    
    if (checked) {
      const newMetricEdge = MetricEdgeProto.create({
        edge_type: MetricEdgeType.METRIC_METRIC_EDGE,
        header: metric,
        source_metric_id: metricInfo?.metrics[0]?.header?.id
      });
      setMetricEdge(prev => [...prev, newMetricEdge]);
    } else {
      setMetricEdge(prev => prev.filter(edge => edge.header?.id !== metric.id));
    }
  };

  const handleBenchmarkChange = (benchmark: Header, checked: boolean) => {
    setSelectedBenchmarkHeader(prev => 
      checked 
        ? [...prev, benchmark]
        : prev.filter(h => h.id !== benchmark.id)
    );
    
    if (checked) {
      const newBenchmarkEdge = MetricEdgeProto.create({
        edge_type: MetricEdgeType.BENCHMARK_EDGE,
        benchmark: {
          header: {
            id: benchmark.id,
            name: benchmark.name
          }
        },
        source_metric_id: metricInfo?.metrics[0]?.header?.id,
        header: benchmark,
      });
      setBenchmarkEdge(prev => [...prev, newBenchmarkEdge]);
    } else {
      setBenchmarkEdge(prev => prev.filter(edge => edge.header?.id !== benchmark.id));
    }
  };

  const handleAttributeChange = (attribute: Header, checked: boolean) => {
    setSelectedAttributeHeader(prev => 
      checked 
        ? [...prev, attribute]
        : prev.filter(h => h.id !== attribute.id)
    );
    
    if (checked) {
      const newAttributeEdge = MetricEdgeProto.create({
        edge_type: MetricEdgeType.METRIC_ATTRIBUTE_EDGE,
        header: attribute,
        source_metric_id: metricInfo?.metrics[0]?.header?.id
      });
      setKgColumnEdge(prev => [...prev, newAttributeEdge]);
    } else {
      setKgColumnEdge(prev => prev.filter(edge => edge.header?.id !== attribute.id));
    }
  };

  const handleMeasureChange = (measure: Header, checked: boolean) => {
    setSelectedMeasureHeader(prev => 
      checked 
        ? [...prev, measure]
        : prev.filter(h => h.id !== measure.id)
    );
    
    if (checked) {
      const newMeasureEdge = MetricEdgeProto.create({
        edge_type: MetricEdgeType.METRIC_MEASURE_EDGE,
        header: measure,
        source_metric_id: metricInfo?.metrics[0]?.header?.id
      });
      setKgColumnEdge(prev => [...prev, newMeasureEdge]);
    } else {
      setKgColumnEdge(prev => prev.filter(edge => edge.header?.id !== measure.id));
    }
  };

  const handleTemporalChange = (temporal: Header, checked: boolean) => {
    setSelectedTemporalHeader(prev => 
      checked 
        ? [...prev, temporal]
        : prev.filter(h => h.id !== temporal.id)
    );
    
    if (checked) {
      const newTemporalEdge = MetricEdgeProto.create({
        edge_type: MetricEdgeType.METRIC_TIMEFRAME_EDGE,
        header: temporal,
        source_metric_id: metricInfo?.metrics[0]?.header?.id
      });
      setKgColumnEdge(prev => [...prev, newTemporalEdge]);
    } else {
      setKgColumnEdge(prev => prev.filter(edge => edge.header?.id !== temporal.id));
    }
  };

  const handleRemoveBenchmarkEdge = (index: number) => {
    const updatedBenchmarkEdges = [...benchmarkEdge];
    updatedBenchmarkEdges.splice(index, 1);
    setBenchmarkEdge(updatedBenchmarkEdges);
  };

  const handleRemoveMetricEdge = (index: number) => {
    const updatedMetricEdges = [...metricEdge];
    updatedMetricEdges.splice(index, 1);
    setMetricEdge(updatedMetricEdges);
  };

  const handleRemoveKgColumnEdge = (id: string) => {
    const updatedKgColumnEdges = kgColumnEdge.filter((edge) => edge.header?.id !== id);
    setKgColumnEdge(updatedKgColumnEdges);
  };

  const handleSubmit = async () => {
    try{
        const updateMetrics = metricInfo?.metrics?.map((metric) => {
            const edgesWithKgColumns = kgColumnEdge && kgColumnEdge?.map((edge) => ({
                edge_type: edge.edge_type,
                header: edge.header,
                source_metric_id: edge.source_metric_id
            }));
    
            const edgesWithBenchmarks = benchmarkEdge && benchmarkEdge?.map((edge) => ({
                edge_type: MetricEdgeType.METRIC_BENCHMARK_EDGE,
                header: edge.header,
                source_metric_id: edge.source_metric_id
            }));
    
            const edgesWithMetrics = metricEdge && metricEdge?.map((edge) => ({
                edge_type: MetricEdgeType.METRIC_METRIC_EDGE,
                header: edge.header,
                source_metric_id: edge.source_metric_id
            }));
    
            const edges = [...edgesWithKgColumns, ...edgesWithBenchmarks, ...edgesWithMetrics];

            return {
                ...metric,
                edges
            };
        });

        const formData= CreateOrUpdateMetrics_Request.create({
            model_id: metricInfo?.model_id || "",
            metrics: updateMetrics as MetricProto[],
            update_edges: true,
        });

        //   API call to create or update the metric
        const responseData = await updateMetric(formData);
        if (responseData) {
          showToast("Metric successfully updated", "", "success");
          onClose();
          // Trigger reload of Knowledge Graph
          if (onReloadKnowledgeGraph) {
            onReloadKnowledgeGraph();
          }
        }
    } catch (error) {
        showToast("Error creating metric", "", "error");
    }
  };

  // Watch for triggerSubmit and call submit when triggered
  useEffect(() => {
    if (triggerSubmit) {
      handleSubmit();
      // Reset the trigger after submitting
      if (setTriggerSubmit) {
        setTriggerSubmit(false);
      }
    }
  },[triggerSubmit]);

  return (
    <EditMKGComponent
      metricInfo={metricInfo}
      allMetrics={allMetrics}
      allBenchmarks={allBenchmarks}
      allKGColumns={allKGColumns}
      metricEdge={metricEdge}
      benchmarkEdge={benchmarkEdge}
      kgColumnEdge={kgColumnEdge}
      selectedMetrics={selectedMetricHeader}
      selectedBenchmarks={selectedBenchmarkHeader}
      selectedAttributes={selectedAttributeHeader}
      selectedMeasures={selectedMeasureHeader}
      selectedTemporals={selectedTemporalHeader}
      onMetricChange={handleMetricChange}
      onBenchmarkChange={handleBenchmarkChange}
      onAttributeChange={handleAttributeChange}
      onMeasureChange={handleMeasureChange}
      onTemporalChange={handleTemporalChange}

      handleRemoveBenchmarkEdge={handleRemoveBenchmarkEdge}
      handleRemoveMetricEdge={handleRemoveMetricEdge}
      handleRemoveKgColumnEdge={handleRemoveKgColumnEdge}

      handleSubmit={handleSubmit}
    />
  );
};

export default EditMKGContainer;